import _ from 'lodash';
import { watch } from 'vue-demi';
import { storeToRefs } from 'pinia';

import { useCommonProps } from './use-common-props';
import { useDialerStore, useDialerGetters, useDialerSetters } from './use-dialer-store';
import { CALL_MEMBER_STATUS } from '../types';
import { SESSION_STATUS } from '@or-sdk/hitl';

/**
 * TODO: At the moment supports only emulated "softphone" conferences.
 * Additional features should be implemented to support WebRTC calls.
 */
export function useDialerReconnectConference() {
  const LOCAL_STORAGE_KEY = 'HITL-Conference-State';
  const props = useCommonProps();
  const store = useDialerStore();
  const getters = useDialerGetters();
  const setters = useDialerSetters();

  const {
    isVoiceAPIReady: isVoiceAPIReadyRef,
    isInProgress: isInProgressRef,
  } = storeToRefs(store);

  const getStorageKey = () => {
    return `${props.agentId.value}-${LOCAL_STORAGE_KEY}`;
  };

  /** Store info that can be used to reconnect to a conference */
  const saveConferenceState = () => {
    props.debug('Save Conference State:', store.sessionId);
    const content = {
      contactId: store.contactId,
      sessionId: store.sessionId,
      identifier: store.identifier,
      tabs: store.tabs,
      membersById: store.membersById,
      controls: store.controls,
      isAgentMuted: store.isAgentMuted,
    };

    localStorage.setItem(getStorageKey(), JSON.stringify(content));
  };

  const clearConferenceState = () => {
    props.debug('Clear Conference State');
    localStorage.removeItem(getStorageKey());
  };

  const attemptToReconnect = async () => {
    const value = localStorage.getItem(getStorageKey());
    if (!value) {
      return;
    }

    const conference = JSON.parse(value);
    props.debug('Trying to Reconnect to a Conference:', conference);

    try {
      const [response, session] = await Promise.all([
        props.getConferenceMembers({ sessionId: conference.sessionId }),
        /* Re-request session to make sure it is available */
        props.requestSessionById(conference.sessionId),
      ]);

      if (!session || session.status === SESSION_STATUS.CLOSED) {
        clearConferenceState();
        return;
      }

      props.debug('Conference Members:', response.data);
      if (!response.data.length) {
        clearConferenceState();
        return;
      }

      /* Mute self if agent was on mute before the reconnect */
      if (conference.isAgentMuted) {
        props.getVoiceAPI().mute();
        store.isAgentMuted = conference.isAgentMuted || false;
      }

      /* We have to manually toggle "Emulate SoftPhone" mode */
      if (conference.controls.emulateSoftPhone) {
        props.getVoiceAPI().switch('softphone');
      }

      const members = _.keyBy(response.data, 'memberId');
      const contact = members[conference.contactId];
      /* Determine conference "On Hold" status based on contact status */
      const isOnHold = contact?.status === CALL_MEMBER_STATUS.ON_HOLD;

      setters.restore(conference);
      setters.setMembers(members);
      store.isContactOnHold = isOnHold;
      store.isInProgress = true;
    } catch (e) {
      console.error('Error while reconnecting to a conference!', e);
      setters.onLeaveCall();
      clearConferenceState();
    }
  };

  const subscribeToReconnectFeature = () => {
    /* Reconnect is attempted only on application start */
    const stopWatchIsVisible = watch(isVoiceAPIReadyRef, async isReady => {
      if (isReady) {
        await attemptToReconnect();
        stopWatchIsVisible();
      }
    }, { immediate: true });

    watch(isInProgressRef, isInProgress => {
      if (isInProgress && store.controls.emulateSoftPhone) {
        saveConferenceState();
      }
    }, { immediate: true });

    /* Clean saved state when conference is done */
    watch(getters.isDialerVisible, (current, previous) => {
      if (!current && previous) {
        clearConferenceState();
      }
    });
  };

  return {
    subscribeToReconnectFeature,
  };
}
